import { useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

/**
 * Normalizes API notification fragments so null/undefined and the literal "undefined" never show in the UI.
 *
 * @param {unknown} value
 * @param {string} fallback
 * @returns {string}
 */
const normalizeNotificationFragment = (value, fallback) => {
  if (value == null) {
    return fallback;
  }
  let s = String(value).trim();
  if (s === '' || s === 'undefined' || s === 'null') {
    return fallback;
  }
  s = s.replace(/\bundefined\b/g, ' ').replace(/\bnull\b/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  return s || fallback;
};

/**
 * useNotifications Hook
 *
 * Manages notifications with real-time WebSocket updates.
 * Handles fetching, marking as read, and deleting notifications.
 * Uses the unified user notifications endpoint for all user types.
 *
 * @param {string|number} userId - The current user's unified ID
 * @returns {Object} - { notifications, markAsRead, deleteNotification }
 */
const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const { t } = useTranslation();
  // eslint-disable-next-line no-undef
  const serverUrl = process.env.REACT_APP_SERVER_URL || '';

  const mapNotificationData = useCallback(
    (data) => ({
      id: data.id,
      header: normalizeNotificationFragment(
        data.action,
        t('notifications_inapp_default_header')
      ),
      details: normalizeNotificationFragment(
        data.event,
        t('notifications_inapp_default_details')
      ),
      unread: data.readAt === null,
      timestamp: data.createdAt,
    }),
    [t]
  );

  // always use unified /user/notifications endpoint
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(
        `${serverUrl}/api/user/notifications/${userId}`,
        {
          withCredentials: true,
        }
      );
      const mappedNotifications = response.data.map(mapNotificationData);
      setNotifications(mappedNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [serverUrl, userId, mapNotificationData]);

  const markAsRead = useCallback(
    async (id) => {
      try {
        await axios.put(
          `${serverUrl}/api/user/notifications/${id}/open`,
          {},
          { withCredentials: true }
        );
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === id
              ? { ...notification, unread: false }
              : notification
          )
        );
      } catch (error) {
        console.error(
          'Error marking notification as read:',
          error.response?.data || error.message
        );
      }
    },
    [serverUrl]
  );

  const deleteNotification = useCallback(
    async (id) => {
      try {
        await axios.delete(`${serverUrl}/api/user/notifications/${id}`, {
          withCredentials: true,
        });
        setNotifications((prev) =>
          prev.filter((notification) => notification.id !== id)
        );
      } catch (error) {
        console.error(
          'Error deleting notification:',
          error.response?.data || error.message
        );
      }
    },
    [serverUrl]
  );

  useEffect(() => {
    if (!userId) return;

    fetchNotifications();

    // connect socket with unified user type for all user types
    const socket = io(serverUrl, {
      query: { userId },
      withCredentials: true,
    });

    const handleNewNotification = (data) => {
      const newNotification = mapNotificationData(data);
      setNotifications((prev) => [newNotification, ...prev]);
    };

    socket.on('notification', handleNewNotification);
    socket.on('reminder', handleNewNotification);

    return () => socket.disconnect();
  }, [userId, serverUrl, fetchNotifications, mapNotificationData]);

  return {
    notifications,
    markAsRead,
    deleteNotification,
    refetch: fetchNotifications,
  };
};

export default useNotifications;
